import { axios } from '/plugins/axios.client';
import { i18n } from '/plugins/i18n.client';

import { toast } from '/plugins/toaster.client.js';

/**
 * Fetch external wizard based on url
 * @param {String} url url of the wizard
 * @param {String} tenantEntryUUID tenant entry UUID
 */
export const getExternalFormDetails = async (url, tenantEntryUUID) => {
  try {
    return await axios.$get('/object/form/external/get', {
      params: { formName: `/${url}`, tenantEntryUUID: tenantEntryUUID }
    });
  } catch (error) {
    return null;
  }
};

/**
 * Fetch Object Template
 * @param {String} templateUUID UUID of the Object Template
 * @param {Object} params includes tenant entry UUID
 */
export const getExternalTemplate = async (templateUUID, params) => {
  try {
    let objectTemplate = await axios.$get(`/objectUI/form/external/${templateUUID}`, {
      params: params
    });
    objectTemplate.conditions = objectTemplate.conditions || '[]';
    objectTemplate.dashboardOptions = objectTemplate.dashboardOptions || '{}';
    objectTemplate.formbuilderJSON = objectTemplate.formbuilderJSON || '[]';
    objectTemplate.formSettingJSON = objectTemplate.formSettingJSON || '{}';
    objectTemplate.dataLoaderSettings = objectTemplate.dataLoaderSettings || '{ "dataLoaderSettings": [] }';

    return {
      ...objectTemplate,
      conditions: objectTemplate.conditions.sParseJSON([]),
      dashboardOptions: objectTemplate.dashboardOptions.sParseJSON({}),
      formbuilderJSON: objectTemplate.formbuilderJSON.sParseJSON([]),
      formSettingJSON: objectTemplate.formSettingJSON.sParseJSON({}),
      dataLoaderSettings: objectTemplate.dataLoaderSettings.sParseJSON({ dataLoaderSettings: [] })
    };
  } catch (error) {
    toast.error(i18n.global.t('common.external.error_fetching_template'));
    return {};
  }
};

/**
 * Fetches translation for given language
 * @param {String} baseLang base language code
 * @param {String} lang language code
 * @param {String} tenantEntryUUID tenant entry UUID
 * @param {String} [formName] optional form URL — scopes dynamic translations to the form's widgets
 */
export const getExternalLanguageTranslations = async (baseLang, lang, tenantEntryUUID, formUrl) => {
  const externalFormUrl = formUrl.startsWith('/') ? formUrl : `/${formUrl}`;
  try {
    return await axios.$get('/api/language/label/json/external', {
      params: { baseLang, lang, tenantEntryUUID, externalFormUrl }
    });
  } catch (error) {
    toast.error(i18n.global.t('common.external.error_fetching_languages'));
    return {};
  }
};

/**
 * Fetch widgets of external Object Template
 * @param {String} templateUUID UUID of the Object Template whose details are to be fetched
 * @param {String} tenantEntryUUID tenant entry UUID
 */
export const getExternalTemplateWidgets = async (templateUUID, tenantEntryUUID) => {
  try {
    return await axios.$get('/api/widgets/loadWidgets/external', {
      params: { pageUUID: templateUUID, tenantEntryUUID: tenantEntryUUID }
    });
  } catch (error) {
    toast.error(i18n.global.t('common.external.error_fetching_widgets'));
    return [];
  }
};

/**
 * Fetch last saved step of the external form
 * @param {String} wizardUUID UUID of the form wizard
 * @param {String} recordUUID UUID of the record
 * @param {String} tenantEntryUUID tenant entry UUID
 */
export const getExternalLastSavedStep = async (wizardUUID, recordUUID, tenantEntryUUID) => {
  try {
    return await axios.$post(`/wizard/form/external/lastSavedStep?tenantEntryUUID=${tenantEntryUUID}`, {
      wizardUUID,
      recordUUID
    });
  } catch (error) {
    toast.error(i18n.global.t('common.external.error_fetching_last_step'));
    return {};
  }
};

/**
 * Update last saved step of the external form
 * @param {String} tenantEntryUUID tenant entry UUID
 * @param {String} recordUUID UUID of the record
 * @param {String} wizardUUID UUID of the form wizard
 * @param {String} stepUUID UUID of the step to be saved
 */
export const updateLastSavedStep = async (tenantEntryUUID, recordUUID, wizardUUID, stepData) => {
  try {
    return await axios.$put(`/wizard/form/external/updateSavedStep?tenantEntryUUID=${tenantEntryUUID}`, {
      recordUUID,
      wizardUUID,
      stepUUID: stepData.stepUUID,
      stepVisitation: stepData.StepVisitation
    });
  } catch (error) {
    toast.error(i18n.global.t('common.external.error_updating_last_step'));
    return null;
  }
};

/**
 * Fetch the external record
 * @param {String} objectUUID UUID of the object attached to form
 * @param {String} recordUUID UUID of the current record
 * @param {String} tenantEntryUUID tenant entry UUID
 */
export const getExternalRecord = async (objectUUID, recordUUID, tenantEntryUUID) => {
  try {
    return await axios.$get(
      `/object/${objectUUID}/get/${recordUUID}?tenantEntryUUID=${tenantEntryUUID}&displayFileSize=true`
    );
  } catch (error) {
    toast.error(i18n.global.t('common.external.error_fetching_ext_record'));
    return null;
  }
};

/**
 * Fetch the external record from view
 * @param {String} objectUUID UUID of the object attached to form
 * @param {String} recordUUID UUID of the current record
 * @param {String} tenantEntryUUID tenant entry UUID
 */
export const getExternalRecordFromView = async (objectUUID, recordUUID, tenantEntryUUID) => {
  try {
    return await axios.$get(`/object/${objectUUID}/record-view/${recordUUID}?tenantEntryUUID=${tenantEntryUUID}`);
  } catch (error) {
    toast.error(i18n.global.t('common.external.error_fetching_ext_record_view'));
    return null;
  }
};
